import { ArrowUpRight } from "lucide-react";

import SmartLink from "@/components/SmartLink";
import { iconRegistry } from "@/content/iconRegistry";
import { siteConfig } from "@/content/site";
import { Button } from "@/components/ui/button";
import { openAnalyticsConsentPreferences } from "@/lib/consent";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-transparent text-muted-foreground py-16 md:py-20 border-t border-border/70" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-card/90 border border-border rounded-3xl p-8 md:p-10 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
            <div>
              <p className="font-sans text-xl md:text-2xl text-foreground font-semibold mb-3">Stay connected</p>
              <p className="font-body text-muted-foreground max-w-xl">
                Subscribe through Beehiiv, browse past issues, and keep up with practical AI and digital skills for real life.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Button asChild className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-6 btn-hover">
                <SmartLink href={siteConfig.beehiivSubscribeUrl} external>
                  Subscribe
                </SmartLink>
              </Button>
              <Button asChild className="rounded-full bg-secondary hover:bg-secondary/85 text-white font-medium px-6 btn-hover">
                <SmartLink href={siteConfig.beehiivPublicationUrl} external>
                  Read the Newsletter
                </SmartLink>
              </Button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="md:col-span-5">
            <h2 className="font-sans text-2xl md:text-3xl font-semibold text-foreground mb-4">
              {siteConfig.brandName}
            </h2>
            <p className="font-body text-muted-foreground leading-relaxed max-w-md">
              {siteConfig.footerTagline}
            </p>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <h3 className="font-sans text-sm font-semibold text-foreground uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {siteConfig.footerLinks.map((link) => (
                <li key={link.label}>
                  <SmartLink
                    href={link.href}
                    external={link.external}
                    className="font-body text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                    data-testid={`footer-${link.label.toLowerCase()}-link`}
                  >
                    {link.label}
                    {link.external ? <ArrowUpRight size={14} /> : null}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="md:col-span-4">
            <h3 className="font-sans text-sm font-semibold text-foreground uppercase tracking-wider mb-4">
              Connect
            </h3>
            <div className="flex gap-4">
              {siteConfig.socialLinks.map((link) => {
                const Icon = iconRegistry[link.icon];

                return (
                  <SmartLink
                    key={link.label}
                    href={link.href}
                    external
                    className="w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:bg-secondary hover:border-secondary hover:text-white transition-all"
                    aria-label={link.label}
                    data-testid={`footer-${link.label.toLowerCase()}-icon`}
                  >
                    <Icon size={20} />
                  </SmartLink>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 flex flex-col gap-4 border-t border-border/70 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="font-body text-sm text-muted-foreground/80 text-center md:text-left">
            © {currentYear} {siteConfig.brandName}. All rights reserved.
          </p>
          <button
            type="button"
            className="font-body text-sm text-muted-foreground/80 transition-colors hover:text-foreground"
            onClick={openAnalyticsConsentPreferences}
          >
            Cookie preferences
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
